/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { RefundType } from 'app/shared/enumeration/refund-type';
import { OrbitTableConfig } from 'app/shared/components/orbit-table/orbit-table.models';

export interface RefundTicketPriceType {
	id: number;
	name: string;
	amount: number;
}

@Component({
	selector: 'refund-ticket-price-type-list',
	templateUrl: './refund-ticket-price-type-list.component.html',
	styleUrls: ['./refund-ticket-price-type-list.component.scss'],
	standalone: false,
})
export class RefundTicketPriceTypeListComponent implements OnInit {
	ngOnInit(): void {
		this.disableControlsIfCustomerRefundType();
	}
	@Input() ticketsFormArray!: FormArray;
	@Input() refundTypeLookupData: any[] = [];

	readonly tableConfig: OrbitTableConfig<FormGroup> = {
		ariaLabel: 'Refund ticket price types', dashboard: false,
		columns: [
			{ key: 'ticketType', label: 'Event Ticket Price Type', value: row => row.value.ticketType, width: '210px', tooltip: true, sortable: false, footerValue: 'Total' },
			{ key: 'ticketNumber', label: 'Ticket Number', value: row => row.value.ticketNumber, width: '150px', sortable: false },
			{
				key: 'ticketPrice',
				label: 'Ticket Price (£)',
				value: row => row.value.ticketPrice,
				width: '140px',
				align: 'right',
				sortable: false,
				footerValue: rows => this.sumBy(rows, 'ticketPrice'),
				footerFormat: value => this.footerNumberFormatter.format(Number(value) || 0),
			},
			{
				key: 'ticketPricePaid',
				label: 'Ticket Price Paid (£)',
				value: row => row.value.ticketPricePaid,
				width: '170px',
				align: 'right',
				sortable: false,
				footerValue: rows => this.sumBy(rows, 'ticketPricePaid'),
				footerFormat: value => this.footerNumberFormatter.format(Number(value) || 0),
			},
			{ key: 'ticketRefundType', label: 'Refund Ticket Price?', value: row => row.value.ticketRefundType, width: '190px', sortable: false, cellClass: 'refund-option-cell' },
			{
				key: 'bookingFee',
				label: 'Booking Fee (£)',
				value: row => row.value.bookingFee,
				width: '140px',
				align: 'right',
				sortable: false,
				footerValue: rows => this.sumBy(rows, 'bookingFee'),
				footerFormat: value => this.footerNumberFormatter.format(Number(value) || 0),
			},
			{
				key: 'bookingFeePaid',
				label: 'Booking Fee Paid (£)',
				value: row => row.value.bookingFeePaid,
				width: '170px',
				align: 'right',
				sortable: false,
				footerValue: rows => this.sumBy(rows, 'bookingFeePaid'),
				footerFormat: value => this.footerNumberFormatter.format(Number(value) || 0),
			},
			{ key: 'bookingFeeRefundType', label: 'Refund Booking Fee?', value: row => row.value.bookingFeeRefundType, width: '190px', sortable: false, cellClass: 'refund-option-cell' },
			{
				key: 'venueLevy',
				label: 'Venue Levy (£)',
				value: row => row.value.venueLevy,
				width: '140px',
				align: 'right',
				sortable: false,
				footerValue: rows => this.sumBy(rows, 'venueLevy'),
				footerFormat: value => this.footerNumberFormatter.format(Number(value) || 0),
			},
			{
				key: 'venueLevyPaid',
				label: 'Venue Levy Paid (£)',
				value: row => row.value.venueLevyPaid,
				width: '170px',
				align: 'right',
				sortable: false,
				footerValue: rows => this.sumBy(rows, 'venueLevyPaid'),
				footerFormat: value => this.footerNumberFormatter.format(Number(value) || 0),
			},
			{ key: 'venueLevyFeeRefundType', label: 'Refund Venue Levy?', value: row => row.value.venueLevyFeeRefundType, width: '190px', sortable: false, cellClass: 'refund-option-cell' },
			{ key: 'paymentStatus', label: 'Payment Status', value: row => row.value.paymentStatus, width: '140px', sortable: false },
			{ key: 'ticketStatus', label: 'Ticket Status', value: row => row.value.ticketStatus, width: '140px', sortable: false },
			{ key: 'dispatchStatus', label: 'Issue Status', value: row => row.value.dispatchStatus, width: '140px', sortable: false },
		],
		rowHover: true,
		selection: { mode: 'disabled', rowId: row => row.value.id ?? row.value.ticketNumber ?? row.value.description ?? this.rowIndex(row) },
		sorting: { enabled: false }, pagination: { enabled: false },
		footer: { enabled: true, sticky: true },
		scroll: { stickyHeader: true, minWidth: '2380px' },
		resultSummary: { visible: false, businessObjectName: 'Ticket', businessObjectNamePlural: 'Tickets' },
		state: { emptyTitle: 'No refund tickets found' },
	};

	private readonly footerNumberFormatter = new Intl.NumberFormat('en-GB', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

	setRefundTypeControls(refundType: RefundType) {
		this.ticketsFormArray.controls.forEach((ticketGroup) => {
			const refundTypeControl = ticketGroup.get('ticketRefundType');
			const bookingFeeRefundTypeControl = ticketGroup.get('bookingFeeRefundType');
			const venueLevyFeeRefundTypeControl = ticketGroup.get('venueLevyFeeRefundType');

			if (refundTypeControl && refundTypeControl.enabled) {
				refundTypeControl.setValue(refundType);
			}
			if (bookingFeeRefundTypeControl && bookingFeeRefundTypeControl.enabled) {
				bookingFeeRefundTypeControl.setValue(refundType);
			}
			if (venueLevyFeeRefundTypeControl && venueLevyFeeRefundTypeControl.enabled) {
				venueLevyFeeRefundTypeControl.setValue(refundType);
			}
		});
	}
	disableControlsIfCustomerRefundType() {
		this.ticketsFormArray.controls.forEach((ticketGroup) => {
			const refundTypeControl = ticketGroup.get('ticketRefundType');
			const bookingFeeRefundTypeControl = ticketGroup.get('bookingFeeRefundType');
			const venueLevyFeeRefundTypeControl = ticketGroup.get('venueLevyFeeRefundType');
			const ticketCanBeRefunded = ticketGroup.get('canRefund')?.value === true;

			if (!ticketCanBeRefunded || refundTypeControl?.value === RefundType.Customer || refundTypeControl?.value === RefundType.Retained) {
				refundTypeControl.disable();
			}
			if (bookingFeeRefundTypeControl?.value === RefundType.Customer || bookingFeeRefundTypeControl?.value === RefundType.Retained) {
				bookingFeeRefundTypeControl.disable();
			}
			if (venueLevyFeeRefundTypeControl?.value === RefundType.Customer || venueLevyFeeRefundTypeControl?.value === RefundType.Retained) {
				venueLevyFeeRefundTypeControl.disable();
			}
		});
	}

	private rowIndex(row: FormGroup): number {
		return this.ticketsFormArray?.controls?.indexOf(row) ?? -1;
	}

	private sumBy(rows: readonly FormGroup[], key: string): number {
		return rows.reduce((sum, row) => sum + (Number(row?.value?.[key]) || 0), 0);
	}

	getTotals(): { [key: string]: number } {
		const totals = {
			ticketPrice: 0,
			ticketPricePaid: 0,
			bookingFee: 0,
			bookingFeePaid: 0,
			venueLevy: 0,
			venueLevyPaid: 0,
		};

		this.ticketsFormArray.controls.forEach((group: FormGroup) => {
			totals.ticketPrice += Number(group.value.ticketPrice) || 0;
			totals.ticketPricePaid += Number(group.value.ticketPricePaid) || 0;
			totals.bookingFee += Number(group.value.bookingFee) || 0;
			totals.bookingFeePaid += Number(group.value.bookingFeePaid) || 0;
			totals.venueLevy += Number(group.value.venueLevy) || 0;
			totals.venueLevyPaid += Number(group.value.venueLevyPaid) || 0;
		});

		return totals;
	}
}
